/**
 * IndexedDB 上传缓存工具类
 * 用于存储和管理 AWS S3 断点续传的缓存数据
 */
class UploadCache {
    static DB_NAME = 'UmuUploadCache';
    static DB_VERSION = 2;
    static STORE_NAME = 'uploads';
    static MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
    static CLEAN_TARGET_RATIO = 0.8; // 清理到80%
    static CLEAN_CHECK_INTERVAL = 60 * 1000; // 60秒检查一次

    static db = null;
    static isSupported = null;
    static lastCleanCheckTime = 0; // 上次清理检查时间

    /**
     * 检查 IndexedDB 是否被支持
     */
    static checkSupport() {
        if (this.isSupported !== null) {
            return this.isSupported;
        }

        // 检查是否在浏览器环境且支持 IndexedDB
        if (typeof window === 'undefined' || !window.indexedDB) {
            console.warn('[UploadCache] IndexedDB is not supported in this environment');
            this.isSupported = false;
            return false;
        }

        this.isSupported = true;
        return true;
    }

    /**
     * 初始化 IndexedDB 数据库
     */
    static async init() {
        if (!this.checkSupport()) {
            throw new Error('IndexedDB is not supported');
        }

        if (this.db) {
            console.log('[UploadCache] DB already initialized, skipping...');
            return;
        }

        console.log('[UploadCache] Initializing IndexedDB...');
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

            request.onerror = () => {
                console.error('[UploadCache] IndexedDB init failed:', request.error);
                reject(new Error(`IndexedDB init failed: ${request.error?.message}`));
            };

            request.onsuccess = async () => {
                this.db = request.result;
                console.log('[UploadCache] IndexedDB initialized successfully');

                // 初始化完成后清理过期记录
                try {
                    const deletedCount = await this.cleanExpiredRecords();
                    if (deletedCount > 0) {
                        console.log(`[UploadCache] Cleaned ${deletedCount} expired records`);
                    }
                } catch (error) {
                    console.warn('[UploadCache] Failed to clean expired records:', error.message);
                }

                resolve();
            };

            request.onupgradeneeded = (event) => {
                console.log('[UploadCache] Database upgrade needed, creating object store...');
                const db = event.target.result;

                if (!db.objectStoreNames.contains(this.STORE_NAME)) {
                    const store = db.createObjectStore(this.STORE_NAME, {
                        keyPath: 'uploadId',
                    });

                    // 创建复合索引用于 teacherId + fileMD5 查找
                    store.createIndex('teacherFileHash', ['teacherId', 'fileMD5'], { unique: false });
                    // 创建时间索引用于清理过期记录
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    console.log('[UploadCache] Object store and indexes created successfully');
                }
            };
        });
    }

    /**
     * 获取数据库实例
     */
    static async getDB() {
        if (!this.db) {
            await this.init();
        }

        if (!this.db) {
            throw new Error('IndexedDB not initialized');
        }

        return this.db;
    }

    /**
     * 计算记录的大小(字节)
     * 使用 Blob 获取更精确的大小估算
     */
    static calculateRecordSize(record) {
        try {
            const blob = new Blob([JSON.stringify(record)]);
            return blob.size;
        } catch (error) {
            // 降级到原有计算方式
            return JSON.stringify(record).length * 2;
        }
    }

    /**
     * 检查并清理缓存
     * 合并了大小检查和清理逻辑，避免双重遍历
     * @returns {Promise<{needClean: boolean, deletedCount: number, totalSize: number}>}
     */
    static async checkAndClean() {
        const db = await this.getDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.STORE_NAME], 'readwrite');
            const store = transaction.objectStore(this.STORE_NAME);
            const index = store.index('createdAt');
            const request = index.openCursor();

            const recordInfos = [];
            let totalSize = 0;

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    const recordSize = this.calculateRecordSize(cursor.value);
                    recordInfos.push({
                        uploadId: cursor.primaryKey,
                        size: recordSize,
                    });
                    totalSize += recordSize;
                    cursor.continue();
                } else {
                    // 判断是否需要清理
                    if (totalSize <= this.MAX_CACHE_SIZE) {
                        resolve({ needClean: false, deletedCount: 0, totalSize });
                        return;
                    }

                    // 执行清理
                    const targetSize = this.MAX_CACHE_SIZE * this.CLEAN_TARGET_RATIO;
                    let currentSize = totalSize;
                    let deletedCount = 0;

                    for (const info of recordInfos) {
                        if (currentSize <= targetSize) break;
                        store.delete(info.uploadId);
                        currentSize -= info.size;
                        deletedCount++;
                    }

                    transaction.oncomplete = () => {
                        resolve({ needClean: true, deletedCount, totalSize });
                    };
                }
            };

            request.onerror = () => {
                reject(new Error(`Check and clean failed: ${request.error?.message}`));
            };

            transaction.onerror = () => {
                reject(new Error(`Transaction failed: ${transaction.error?.message}`));
            };
        });
    }

    /**
     * 保存上传记录
     */
    static async saveRecord(record) {
        const db = await this.getDB();

        // 先保存记录
        await new Promise((resolve, reject) => {
            const transaction = db.transaction([this.STORE_NAME], 'readwrite');
            const store = transaction.objectStore(this.STORE_NAME);
            const request = store.put(record);

            request.onsuccess = () => resolve();
            request.onerror = () => {
                reject(new Error(`Save record failed: ${request.error?.message}`));
            };
        });

        // 节流检查：避免频繁触发清理
        const now = Date.now();
        if (now - this.lastCleanCheckTime < this.CLEAN_CHECK_INTERVAL) {
            return;
        }
        this.lastCleanCheckTime = now;

        // 检查缓存大小并清理
        try {
            const { needClean, deletedCount, totalSize } = await this.checkAndClean();
            if (needClean) {
                console.log(
                    `[UploadCache] Cache size exceeded (${(totalSize / 1024 / 1024).toFixed(
                        2
                    )}MB), cleaned ${deletedCount} old records`
                );
            }
        } catch (error) {
            console.warn('[UploadCache] Failed to check/clean cache:', error.message);
        }
    }

    /**
     * 根据 teacherId 和 fileMD5 查找记录
     */
    static async findRecord(teacherId, fileMD5) {
        const db = await this.getDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.STORE_NAME], 'readonly');
            const store = transaction.objectStore(this.STORE_NAME);
            const index = store.index('teacherFileHash');
            const request = index.get([teacherId, fileMD5]);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => {
                reject(new Error(`Find record failed: ${request.error?.message}`));
            };
        });
    }

    /**
     * 根据 uploadId 获取记录
     */
    static async getRecord(uploadId) {
        const db = await this.getDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.STORE_NAME], 'readonly');
            const store = transaction.objectStore(this.STORE_NAME);
            const request = store.get(uploadId);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => {
                reject(new Error(`Get record failed: ${request.error?.message}`));
            };
        });
    }

    /**
     * 更新分片信息
     */
    static async updatePart(uploadId, part) {
        const record = await this.getRecord(uploadId);
        if (!record) {
            throw new Error(`Upload record not found: ${uploadId}`);
        }

        // 初始化 parts 数组（如果不存在）
        if (!record.parts) {
            record.parts = [];
        }

        // 查找现有分片或添加新分片
        const existingPartIndex = record.parts.findIndex((p) => p.PartNumber === part.PartNumber);
        if (existingPartIndex >= 0) {
            record.parts[existingPartIndex] = part;
        } else {
            record.parts.push(part);
        }

        // 按分片序号排序
        record.parts.sort((a, b) => a.PartNumber - b.PartNumber);

        await this.saveRecord(record);
    }

    /**
     * 删除记录
     */
    static async deleteRecord(uploadId) {
        const db = await this.getDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.STORE_NAME], 'readwrite');
            const store = transaction.objectStore(this.STORE_NAME);
            const request = store.delete(uploadId);

            request.onsuccess = () => resolve();
            request.onerror = () => {
                reject(new Error(`Delete record failed: ${request.error?.message}`));
            };
        });
    }

    /**
     * 清理过期记录
     * @param maxAge 最大缓存时间(毫秒)，默认7天
     */
    static async cleanExpiredRecords(maxAge = 7 * 24 * 60 * 60 * 1000) {
        const db = await this.getDB();
        const cutoffTime = Date.now() - maxAge;
        let deletedCount = 0;

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.STORE_NAME], 'readwrite');
            const store = transaction.objectStore(this.STORE_NAME);
            const index = store.index('createdAt');
            const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    cursor.delete();
                    deletedCount++;
                    cursor.continue();
                } else {
                    resolve(deletedCount);
                }
            };

            request.onerror = () => {
                reject(new Error(`Clean expired records failed: ${request.error?.message}`));
            };
        });
    }

    /**
     * 关闭数据库连接
     */
    static close() {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
    }
}

module.exports = { UploadCache };
